<script>
  import InfoTile from '../InfoTile/InfoTile.svelte';
  import HeaderExpander from '../HeaderExpander/HeaderExpander.svelte';
  import HeaderDropdown from '../HeaderDropdown/HeaderDropdown.svelte';
  import UseFocusTrap from '../../utils/UseFocusTrap.svelte';
  import UseEscapeKeydown from '../../utils/UseEscapeKeydown.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import {
    isHeaderMenuOpen,
    closeHeaderMenu,
    openHeaderMenu,
    headerMenuBinding,
    siteNoticeBinding,
    siteNoticeShow,
  } from '../../stores/headerMenu.ts';
  import { actions } from '../../utils/actions';
  import { openOverpanel } from '../../stores/overpanelControl';
  import { buildURL } from '../../utils/route-utils';
  import { accessCode } from '../../stores/referrer';

  let {
    skipLinkLabel,
    url,
    logoImage,
    getCardLabel,
    searchLabel,
    menuItems,
    englishLabel,
    spanishLabel,
    infoTiles = [],
    lang = 'en',
    path = '',
    menuButtonOpenA11yTemplate,
    menuButtonCloseA11yText,
    query = {},
    spanishLink = undefined,
    englishLink = undefined,
    mainContentId = 'main-content',
    dataTestId = 'the-header',
  } = $props();
  
  let skipLinkUrl = $derived(`${buildURL(path, query)}#${mainContentId}`);

  const isSavingsCard = path.match(/\/savings-card/) || path.match(/\/discount-card/);

  // to respond to changes in the English and Spanish link props
  let englishLinkObj = $derived(englishLink);
  let spanishLinkObj = $derived(spanishLink);

  let getCardLink = $derived(
    `${lang === 'es' ? '/es/savings-card' : '/savings-card'}${$accessCode ? `?source=${$accessCode}` : ''}`
  );

  let menuExpander = $state();
  let hasBeenOpenedOnce = $state(false);
  let headerContainer = $state();

  siteNoticeShow.subscribe(() => {
    if (menuExpander) {
      const siteNoticeHeight = $siteNoticeBinding && $siteNoticeShow ? $siteNoticeBinding.offsetHeight || 0 : 0;
      menuExpander.style.height = `${window.innerHeight - (siteNoticeHeight + $headerMenuBinding.offsetHeight)}px`;
    }
  });

  // When the header menu is shown, scroll user to top of page
  // Disable scroll on main body container
  // Trap focus inside header when menu is open
  let toggleShowHeaderMenu = (toggleState) => {
    hasBeenOpenedOnce = true;

    if (toggleState) {
      openHeaderMenu();
      window.scrollTo(0, 0);
      const siteNoticeHeight = $siteNoticeBinding ? $siteNoticeBinding.offsetHeight || 0 : 0;
      menuExpander.style.height = `${window.innerHeight - (siteNoticeHeight + $headerMenuBinding.offsetHeight)}px`;
    } else {
      closeHeaderMenu();
    }
  };
</script>

<svelte:head>
  {#if logoImage}
    <link rel="preload" as="image" href={logoImage.url} />
  {/if}
</svelte:head>

{#if $isHeaderMenuOpen && headerContainer}
  <UseEscapeKeydown container={headerContainer} onEscapeHandler={() => toggleShowHeaderMenu(false)} />
  <UseFocusTrap container={headerContainer} />
{/if}
<header class="relative print:hidden" bind:this={headerContainer} data-testid={dataTestId}>
  <BaseLink
    url={skipLinkUrl}
    className="skip-link absolute left-0 m-4 p-2 bg-white border border-gray rounded-sm -z-1 focus:z-50"
  >
    {skipLinkLabel}
  </BaseLink>
  <nav bind:this={$headerMenuBinding} class="relative bg-blue-navy shadow-dark z-30">
    <div class="relative flex justify-between items-center px-4 h-16 max-w-6xl mx-auto">
      {#if logoImage}
        <BaseLink className="lg:mr-6" url={url.url}>
          <img
            data-testid="TheHeader-logo"
            class="w-32 h-6 shrink-0"
            src={logoImage.url}
            alt={logoImage.alt}
            height="24"
            width="128"
          />
        </BaseLink>
      {/if}
      <div class="h-full flex-1 flex-row hidden lg:block">
        {#each menuItems as { ...item }, index (index)}
          {#if item.__typename === 'BaseLink'}
            <BaseLink
              url={item.url}
              className="h-full leading-tight font-semibold text-white whitespace-nowrap px-4 hover-hover:hover:underline active:underline"
              onClick={() => window.umami.track(`header-link${item.url.replace(/\//g, '-')}`)}
            >
              {item.text}
            </BaseLink>
          {:else if item.__typename === 'BaseAction'}
            <button
              class="h-full leading-tight font-semibold text-white whitespace-nowrap px-4 hover-hover:hover:underline active:underline" 
              onclick={() => actions(item.event)}
              data-umami-event={`header-link-${item.event}`}
            >
              {item.text}
            </button>
          {:else if item.__typename === 'HeaderExpander'}
            <HeaderDropdown {...item} />
          {/if}
        {/each}
      </div>
      <div class="flex items-center">
        {#if !$isHeaderMenuOpen}
          {#if !isSavingsCard}
            <BaseLink
              dataTestId="TheHeader-get-card-button"
              className="mr-4 p-2 bg-blue-cerulean hover-hover:hover:bg-blue-darkish hover-hover:hover:border-blue-darkish active:bg-blue-darkish active:shadow-active rounded-sm text-xs text-white lg:hidden"
              url={getCardLink}
              onClick={() => window.umami.track(`header-mobile-btn-${lang === 'es' ? 'es-' : ''}savings-card`)}
            >
              {getCardLabel}
            </BaseLink>
          {/if}
          <button
            class="p-1 mr-4 flex items-center lg:mr-0 lg:px-6 lg:py-2 lg:rounded-full lg:shadow-md lg:bg-blue-cerulean focus:outline-hidden
            focus:bg-blue-darkish focus:shadow-focus active:shadow-active hover-hover:hover:bg-blue-darkish active:bg-blue-darkish lg:max-w-xs"
            aria-label={searchLabel}
            onclick={() => {
              openOverpanel('search');
              window.umami.track(`header-mobile-btn-${lang === 'es' ? 'es-' : ''}search`);
            }}
          >
            <div class="inline-block w-6 h-6 lg:w-4 lg:h-4 lg:mr-3">
              <svg class="fill-current text-white w-full h-full">
                <use xlink:href={`${SPRITESHEET_URL}#magnifying-glass`} />
              </svg>
            </div>
            <div class="text-white hidden lg:inline-block">{searchLabel}</div>
          </button>
        {/if}
        <button
          id="menuButton"
          data-testid="TheHeader-hamburger-menu"
          class="w-8 h-8 flex justify-center items-center p-1 lg:hidden hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-hidden
          focus:bg-blue-darkish focus:shadow-focus active:shadow-active"
          onclick={() => toggleShowHeaderMenu(!$isHeaderMenuOpen)}
          aria-haspopup="true"
          aria-expanded={$isHeaderMenuOpen}
          aria-label={$isHeaderMenuOpen
            ? menuButtonOpenA11yTemplate.replace(/{{ITEMS}}/, menuItems.length)
            : menuButtonCloseA11yText}
        >
          <div
            class="animated-menu-icon a11y-no-motion"
            class:is-expanded={$isHeaderMenuOpen}
            class:is-collapsed={hasBeenOpenedOnce && !$isHeaderMenuOpen}
          ></div>
        </button>
      </div>
    </div>
  </nav>
  <nav
    data-testid="TheHeader-menu"
    bind:this={menuExpander}
    class="flex fixed flex-col w-screen mt-0 bg-blue-navy overflow-auto z-30 lg:hidden"
    class:hidden={!$isHeaderMenuOpen}
    class:block={$isHeaderMenuOpen}
    aria-label="Main menu"
  >
    <div
      data-testid="TheHeader-language-switcher"
      class="flex flex-row items-center text-lg text-white px-6 py-5 leading-tight"
    >
      <div class="w-6 h-6 mr-4">
        <svg class="fill-current w-full h-full">
          <use xlink:href={`${SPRITESHEET_URL}#language-globe`} />
        </svg>
      </div>
      {#if englishLinkObj && englishLinkObj.url}
        <BaseLink
          className="mr-4 inline-block text-lg text-white opacity-50 hover-hover:hover:opacity-75 active:opacity-100"
          url={englishLinkObj.url}
          target={englishLinkObj.target}
          dataTestId="the-footer-language-en"
        >
          {englishLabel}
        </BaseLink>
      {:else}
        <span
          class="mr-4 inline-block text-lg text-white border-white border-b-2 font-semibold"
          data-testid="the-footer-language-en"
        >
          {englishLabel}
        </span>
      {/if}
      {#if spanishLinkObj && spanishLinkObj.url}
        <BaseLink
          className="mr-4 inline-block text-lg text-white opacity-50 hover-hover:hover:opacity-75 active:opacity-100"
          url={spanishLinkObj.url}
          target={spanishLinkObj.target}
          dataTestId="the-footer-language-es"
        >
          {spanishLabel}
        </BaseLink>
      {:else}
        <span
          class="mr-4 inline-block text-lg text-white border-white border-b-2 font-semibold"
          data-testid="the-footer-language-es"
        >
          {spanishLabel}
        </span>
      {/if}
    </div>
    <div class="flex flex-1 flex-col space-between">
      <div class="flex-1 bg-white">
        {#each menuItems as item, index (index)}
          {#if item.__typename === 'BaseLink'}
            {#if path === item.url || path === `${item.url}/`}
              <button
                class="w-full flex items-center px-6 py-5 text-blue-cerulean bg-white border-b border-gray-opaque-charcoal hover-hover:hover:underline
                  hover-hover:hover:text-blue-darkish active:underline active:text-blue-darkish focus:relative focus:outline-hidden active:shadow-inset-active focus:shadow-inset-focus"
                onclick={() => closeHeaderMenu()}
              >
                {#if item.icon}
                  <img
                    class="block w-8 h-8 shrink-0"
                    role="presentation"
                    src={item.icon.url}
                    alt={item.icon.alt}
                    height="32"
                    width="32"
                  />
                {/if}
                <p class="text-lg font-semibold text-left grow px-6" class:ml-8={!item.icon}>{item.text}</p>
              </button>
            {:else}
              <BaseLink
                url={item.url}
                className="w-full flex items-center px-6 py-5 text-blue-cerulean bg-white border-b active:underline active:text-blue-darkish
                border-gray-opaque-charcoal hover-hover:hover:underline hover-hover:hover:text-blue-darkish focus:relative 
                focus:outline-hidden active:shadow-inset-active focus:shadow-inset-focus"
                onClick={() => window.umami.track(`header-mobile-link${item.url.replace(/\//g, '-')}`)}
              >
                {#if item.icon}
                  <img
                    class="block w-8 h-8 shrink-0"
                    role="presentation"
                    src={item.icon.url}
                    alt={item.icon.alt}
                    height="32"
                    width="32"
                  />
                {/if}
                <p class="text-lg font-semibold text-left grow px-6" class:ml-8={!item.icon}>{item.text}</p>
              </BaseLink>
            {/if}
          {:else if item.__typename === 'BaseAction'}
            <button
              class="w-full flex items-center px-6 py-5 text-blue-cerulean bg-white border-b border-gray-opaque-charcoal active:underline active:text-blue-darkish
              hover-hover:hover:underline hover-hover:hover:text-blue-darkish focus:relative focus:outline-hidden active:shadow-inset-active focus:shadow-inset-focus"
              onclick={() => {
                actions(item.event);
                window.umami.track(`header-mobile-link-${lang === 'es' ? 'es-' : ''}${item.event}`);
              }}
            >
              {#if item.icon}
                <img
                  class="block w-8 h-8 shrink-0"
                  role="presentation"
                  src={item.icon.url}
                  alt={item.icon.alt}
                  height="32"
                  width="32"
                />
              {/if}
              <p class="text-lg font-semibold text-left grow px-6" class:ml-8={!item.icon}>{item.text}</p>
            </button>
          {:else if item.__typename === 'HeaderExpander'}
            <HeaderExpander {...item} {path} />
          {/if}
        {/each}
      </div>
      <div>
        {#each infoTiles as ad (ad.id)}
          <InfoTile {...ad} />
        {/each}
      </div>
    </div>
  </nav>
</header>
